import axios from 'axios'
import { registerUrl } from '@/plugins/auth'
import { Category, UnryoNode, UnryoIssue } from '@/model'
import { buildUrl } from '@/services/path'

/**
 * Finds the related url of a node.
 *
 * @param node The node to check.
 * @returns The url of the node.
 */
function url (node: UnryoNode): string | undefined {
  const role = node.roles.find((item) => {
    return item.parameters && item.parameters.$url
  })
  if (role) {
    return role.parameters.$url
  }
}

/**
 * Hide random part of a node id.
 *
 * @param id The node id to hide the random part.
 * @returns The node id without the random part.
 */
export function nodeIdToDisplayName (id: string): string {
  return id.replace(/(-[0-9a-f]{8})?-[0-9a-f]{33,}$/, '')
}

/**
 * Validate a relation to another relation or an array of relation.
 *
 * @param {string[]|string} a relation 'a' as a string or array to validate from
 * @param {string} b relation 'b' to validate and compare to relation 'a'
 * @returns whether or not relation b equals to relation a (if string), or b is included in relation a (if array).
 */
function validateRelation (a: string[] | string, b: string): boolean {
  if (Array.isArray(a)) {
    for (const item of a) {
      if (item === b) {
        return true
      }
    }
    return false
  }
  return a === b
}

/**
 * Define if a group is valid from his requirements.
 *
 * @param req possible group requirements.
 * @returns whether or not the group requirements are valid.
 */
function validateGroupReq (req: Array<{ relation: string | string[], role: string }>) {
  return (node: UnryoNode) => {
    const roles = node.roles
    if (roles.length < req.length) {
      return false
    }
    for (const item of req) {
      let found = false
      for (const role of node.roles) {
        if (validateRelation(item.relation, role.relation) && role.role === item.role) {
          found = true
          break
        }
      }
      if (!found) {
        return false
      }
    }
    return true
  }
}

export type Group = ('collector' | 'database' | 'report' | 'portal' | 'analytic' | 'vault')

/**
 * Holds functions that validate or not a group of a node.
 */
const groups: Record<Group, (node: UnryoNode) => boolean> = {
  collector: validateGroupReq([
    { relation: ['collect', 'collect-internal'], role: 'producer' }
  ]),
  database: validateGroupReq([
    { relation: ['collect', 'collect-internal'], role: 'receiver' },
    { relation: ['report', 'report-internal'], role: 'supplier' }
  ]),
  report: validateGroupReq([
    { relation: ['report', 'report-internal'], role: 'consumer' }
  ]),
  portal: validateGroupReq([
    { relation: ['portal', 'portal-internal'], role: 'consumer' }
  ]),
  analytic: validateGroupReq([
    { relation: ['analytic', 'analytic-internal'], role: 'consumer' }
  ]),
  vault: validateGroupReq([
    { relation: ['manage', 'manage-internal'], role: 'supplier' }
  ])
}

/**
 * Finds the group a node belongs to.
 *
 * @param node - The node to check.
 * @returns The group the node belongs to, or undefined if none applies.
 */
export function nodeGroup (node: UnryoNode): Group | undefined {
  for (const [group, validate] of Object.entries(groups)) {
    if (validate(node)) {
      return group as Group
    }
  }
}

/**
 * From an item, it returns a node with only 1 depth.
 *
 * @param item The item to convert.
 * @returns The converted item into a node.
 */
export function toNode (item: any): UnryoNode {
  let count = null
  if (item.metadata.usage) {
    count = item.metadata.usage.count
  }

  const node: UnryoNode = {
    id: item.node.id,
    uid: item.node.id + '@' + item.node.namespace,
    label: nodeIdToDisplayName(item.node.id),
    namespace: item.node.namespace,
    roles: item.node.roles,
    timestamp: item.timestamp,
    url: url(item.node),
    metadata: {
      externalIp: stripPort(item.metadata.externalIp || ''),
      version: item.metadata.version,
      usage: {
        count: count
      }
    },
    health: {
      status: item.health.status,
      availabilityIssue: toUnryoIssue(Category.AVAILABILITY, item.health.issues),
      performanceIssue: toUnryoIssue(Category.PERFORMANCE, item.health.issues)
    }
  }
  node.group = nodeGroup(node)
  return node
}

function stripPort (ipMaybe: string): string {
  if (v46Exact.test(ipMaybe)) {
    return ipMaybe
  }
  return ipMaybe.replace(/:\d+$/, '').replace(/^\[(.+)\]$/, (_, b) => b)
}

const v4 = '(?:25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]\\d|\\d)(?:\\.(?:25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]\\d|\\d)){3}';

const v6segment = '[a-fA-F\\d]{1,4}';

const v6 = `
(?:
(?:${v6segment}:){7}(?:${v6segment}|:)|                                    // 1:2:3:4:5:6:7::  1:2:3:4:5:6:7:8
(?:${v6segment}:){6}(?:${v4}|:${v6segment}|:)|                             // 1:2:3:4:5:6::    1:2:3:4:5:6::8   1:2:3:4:5:6::8  1:2:3:4:5:6::1.2.3.4
(?:${v6segment}:){5}(?::${v4}|(?::${v6segment}){1,2}|:)|                   // 1:2:3:4:5::      1:2:3:4:5::7:8   1:2:3:4:5::8    1:2:3:4:5::7:1.2.3.4
(?:${v6segment}:){4}(?:(?::${v6segment}){0,1}:${v4}|(?::${v6segment}){1,3}|:)| // 1:2:3:4::        1:2:3:4::6:7:8   1:2:3:4::8      1:2:3:4::6:7:1.2.3.4
(?:${v6segment}:){3}(?:(?::${v6segment}){0,2}:${v4}|(?::${v6segment}){1,4}|:)| // 1:2:3::          1:2:3::5:6:7:8   1:2:3::8        1:2:3::5:6:7:1.2.3.4
(?:${v6segment}:){2}(?:(?::${v6segment}){0,3}:${v4}|(?::${v6segment}){1,5}|:)| // 1:2::            1:2::4:5:6:7:8   1:2::8          1:2::4:5:6:7:1.2.3.4
(?:${v6segment}:){1}(?:(?::${v6segment}){0,4}:${v4}|(?::${v6segment}){1,6}|:)| // 1::              1::3:4:5:6:7:8   1::8            1::3:4:5:6:7:1.2.3.4
(?::(?:(?::${v6segment}){0,5}:${v4}|(?::${v6segment}){1,7}|:))             // ::2:3:4:5:6:7:8  ::2:3:4:5:6:7:8  ::8             ::1.2.3.4
)(?:%[0-9a-zA-Z]{1,})?                                             // %eth0            %1
`.replace(/\s*\/\/.*$/gm, '').replace(/\n/g, '').trim();

// Pre-compile only the exact regexes because adding a global flag make regexes stateful
const v46Exact = new RegExp(`(?:^${v4}$)|(?:^${v6}$)`);

function toUnryoIssue (category: string, issues: UnryoIssue[]): UnryoIssue | undefined {
  if (issues) {
    for (const issue of issues) {
      if (issue.category === category) {
        return issue
      }
    }
  }
}

const BROKER_API_URL = buildUrl(process.env.VUE_APP_BROKER_URL, '/api/v1')
registerUrl(BROKER_API_URL)

export default {

  /**
   * Checks whether network access has been granted or not.
   *
   * @returns A promise resolving if the access is granted.
   */
  access () {
    return axios.get(buildUrl(BROKER_API_URL,'namespaces'))
  },

  /**
   * Fetches all unryo access token of a namespace.
   *
   * @param namespace unryo namespace.
   * @returns A promise resolving to a list of all unryo token of a namespace.
   */
  namespaceTokens (namespace: string) {
    return axios.get(buildUrl(BROKER_API_URL, 'namespaces/' + namespace + '/tokens'))
      .then((response: any) => response.data.value)
  },

  /**
   * Create a token for a specified namespace.
   *
   * @param namespace unryo namespace.
   * @returns A promise resolving to the creation status.
   */
  createToken (namespace: string, body: object | null | undefined) {
    return axios.post(buildUrl(BROKER_API_URL, 'namespaces/' + namespace + '/tokens'), body)
      .then((response: any) => response.data.value)
  },

  /**
   * Delete a token with his id.
   *
   * @param tokenId id of the token to delete.
   * @param namespace unryo namespace associated to the token.
   * @returns A promise resolving the deletion status.
   */
  deleteToken (namespace: string, tokenId: string) {
    return axios.delete(buildUrl(BROKER_API_URL, 'namespaces/' + namespace + '/tokens/' + tokenId))
      .then((response: any) => response.data.value)
  },

  /**
   * Edit a token with his id.
   *
   * @param tokenId id of the token to edit.
   * @param namespace unryo namespace associated to the token.
   * @param data object with the new token expiration.
   * @returns A promise resolving the editing status.
   */
  editToken (namespace: string, tokenId: string, data: any) {
    return axios.patch(buildUrl(BROKER_API_URL, 'namespaces/' + namespace + '/tokens/' + tokenId), data)
      .then((response: any) => response.data.value)
  },

  /**
   * Lists the elements the current user can access to.
   *
   * @param namespace An optional namespace with which to filter the nodes.
   * @returns A promise resolving to the list of nodes.
   */
  nodes (namespace?: string) {
    return axios.get(buildUrl(BROKER_API_URL, 'nodes'))
      .then((response: any) => {
        const data = response.data.value
        const filteredNodes = namespace ? data.filter((item: any) => {
          return item.node.namespace === namespace
        }) : data
        return filteredNodes.map(toNode)
      })
  },

  /**
   * Lists the current existing namespaces.
   *
   * @returns A promise resolving to the list of existing namespaces.
   */
  namespaces () {
    return axios.get(buildUrl(BROKER_API_URL, 'namespaces'))
      .then((response: any) => {
        const values = response.data.value.map((namespace: any) => {
          return namespace.id
        }).sort((a: string, b: string) => {
          return a.toLowerCase().localeCompare(b.toLowerCase())
        })
        return values
      })
  },

  /**
   * Create a new namespace.
   *
   * @param namespace unryo namespace.
   * @returns A promise resolving the created namespace.
   */
  createNamespace (namespace: string) {
    return axios.put(buildUrl(BROKER_API_URL, 'namespaces/' + namespace), null)
      .then((response) => response.data.value)
  },

  /**
   * Delete a namespace.
   *
   * @param namespace unryo namespace.
   * @returns A promise resolving the deletion status.
   */
  deleteNamespace (namespace: string) {
    return axios.delete(buildUrl(BROKER_API_URL,'namespaces/' + namespace))
      .then((response) => response.data.value)
  }
}
